import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { profileService } from "@/services/profileService";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, ArrowLeft, Loader2, Mail, AlertTriangle, ShieldCheck } from "lucide-react";

export default function Upgrade() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleRegisterInterest = async () => {
    if (!user?.id || !profile) return;
    
    setLoading(true);
    try {
      // Save interest in profile metadata
      const currentMetadata = (profile as any).metadata || {};
      const updatedMetadata = {
        ...currentMetadata,
        premiumInterest: true,
        premiumInterestRegisteredAt: new Date().toISOString()
      };

      await profileService.updateProfile(user.id, {
        metadata: updatedMetadata
      } as any);

      // Refresh local auth context to sync profile
      await refreshProfile();
      setRegistered(true);
    } catch (err) {
      console.error("[Upgrade] Failed to register premium interest:", err);
    } finally {
      setLoading(false);
    }
  };

  const premiumFeatures = [
    t("upgrade.features.unlimitedTests", "Simulados e testes ilimitados (CE, CO, EE)"),
    t("upgrade.features.aiCorrection", "Correção avançada de redações com Inteligência Artificial"),
    t("upgrade.features.srsFlashcards", "Flashcards inteligentes com revisão espaçada (SRS)"),
    t("upgrade.features.errorBank", "Acesso total ao Banco de Erros ativo"),
    t("upgrade.features.fullHistory", "Histórico completo e métricas qualitativas de desempenho"),
    t("upgrade.features.prioritySupport", "Suporte prioritário e dicas pedagógicas personalizadas")
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12 animate-in fade-in duration-500">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
      >
        <ArrowLeft className="w-4.5 h-4.5" />
        {t("common.back", "Voltar")}
      </Button>

      {/* Hero Header */}
      <div className="text-center space-y-3 py-6 max-w-2xl mx-auto">
        <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-extrabold px-3 py-1 text-[10px] uppercase border-none tracking-widest animate-pulse">
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          {t("upgrade.badge", "Plano Premium em Breve")}
        </Badge>
        <h1 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tight leading-none pt-1">
          {t("upgrade.title", "Eleve sua Preparação para o TEF Canada")}
        </h1>
        <p className="text-sm font-medium leading-relaxed text-slate-500">
          {t("upgrade.subtitle", "Desbloqueie acesso total e ilimitado às melhores ferramentas de estudo e análises qualitativas baseadas em IA.")}
        </p>
      </div>

      {registered ? (
        <Card className="border-none shadow-xl bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/30 overflow-hidden rounded-3xl p-6 text-center max-w-xl mx-auto">
          <CardContent className="space-y-5 py-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-md">
              <ShieldCheck className="h-8 w-8" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-800">
                {t("upgrade.successTitle", "Interesse Registrado!")}
              </h2>
              <p className="text-xs font-semibold text-slate-500 leading-relaxed max-w-md mx-auto">
                {t("upgrade.interestSuccess", "Muito obrigado pelo interesse! Registramos seu contato e você será notificado com exclusividade e descontos de lançamento assim que o Plano Premium estiver disponível.")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs px-6 py-4.5 mt-2"
            >
              {t("upgrade.backDashboard", "Voltar ao Dashboard")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 pt-2 items-stretch">
          {/* Plano Gratuito */}
          <Card className="border-slate-200 shadow-sm bg-white p-6 rounded-2xl flex flex-col justify-between">
            <div className="space-y-4">
              <div className="space-y-1">
                <span className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                  {t("upgrade.freeTier", "Plano Atual")}
                </span>
                <CardTitle className="text-2xl font-black text-slate-800">
                  {t("upgrade.freeTitle", "Plano Gratuito")}
                </CardTitle>
                <CardDescription className="text-xs font-medium text-slate-400">
                  {t("upgrade.freeDesc", "Treinamento básico para conhecer o formato da prova.")}
                </CardDescription>
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-3 text-xs text-slate-600 font-semibold">
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t("upgrade.freeFeatures.testsLimit", "Limite de até 4 provas completas ou simulados")}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t("upgrade.freeFeatures.basicFlashcards", "Flashcards de erros criados a partir do histórico")}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t("upgrade.freeFeatures.vocabulary", "Banco de Palavras padrão para vocabulário")}</span>
                </div>
                <div className="flex items-center gap-2.5 opacity-55">
                  <Check className="w-4 h-4 text-slate-300 shrink-0" />
                  <span className="line-through">{t("upgrade.freeFeatures.noAi", "Análise e correções com Inteligência Artificial")}</span>
                </div>
              </div>
            </div>
            
            <div className="pt-8">
              <Button
                variant="outline"
                disabled
                className="w-full h-11 border-slate-200 text-slate-400 font-bold rounded-xl text-xs"
              >
                {t("upgrade.activePlan", "Plano Ativo")}
              </Button>
            </div>
          </Card>

          {/* Plano Premium */}
          <Card className="border-none shadow-md bg-gradient-to-br from-violet-600 via-indigo-600 to-indigo-700 text-white p-1 rounded-2xl flex flex-col overflow-hidden relative">
            {/* Ribbon */}
            <div className="absolute right-0 top-0 bg-amber-400 text-slate-900 font-extrabold text-[9px] uppercase px-3 py-1 rounded-bl-xl tracking-wider shadow-sm flex items-center gap-1">
              <Sparkles className="w-3 h-3 fill-slate-900" />
              <span>{t("upgrade.premiumRibbon", "Recomendado")}</span>
            </div>

            <div className="p-6 flex-1 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="space-y-1">
                  <span className="text-xs font-bold uppercase text-purple-200 tracking-wider">
                    {t("upgrade.premiumTier", "Completo & Ilimitado")}
                  </span>
                  <CardTitle className="text-2xl font-black text-white">
                    {t("upgrade.premiumTitle", "TEF Trainer Premium")}
                  </CardTitle>
                  <CardDescription className="text-xs font-medium text-purple-100">
                    {t("upgrade.premiumDesc", "Acesso ilimitado e sem barreiras a todas as funcionalidades.")}
                  </CardDescription>
                </div>

                <div className="border-t border-indigo-500/50 pt-4 space-y-3.5 text-xs text-purple-50">
                  {premiumFeatures.map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-2.5 font-semibold">
                      <Check className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-8">
                <Button
                  onClick={handleRegisterInterest}
                  disabled={loading}
                  className="w-full h-12 bg-amber-400 hover:bg-amber-350 text-slate-900 font-black rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg hover:scale-[1.01] transition-all border-none"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-900" />
                  ) : (
                    <Mail className="w-4 h-4 text-slate-900" />
                  )}
                  {t("upgrade.notifyButton", "Tenho Interesse / Notifique-me")}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Disclaimers */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-3 items-start text-[11px] font-semibold text-slate-500 leading-normal max-w-2xl mx-auto mt-4">
        <AlertTriangle className="w-4 h-4 shrink-0 text-slate-400 mt-0.5" />
        <span>
          {t("upgrade.disclaimer", "Por enquanto, a plataforma está em fase beta. Nenhum valor de pagamento será cobrado agora. Ao registrar seu interesse, garantiremos sua vaga promocional no lançamento oficial!")}
        </span>
      </div>
    </div>
  );
}
